import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Check } from 'lucide-react';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QUOTE_COOLDOWN = 60;

const QuoteModal: React.FC<QuoteModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', type: 'bulk', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const loadedAt = React.useRef(Date.now());

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Reset load time when modal opens
  useEffect(() => {
    if (isOpen) loadedAt.current = Date.now();
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Timing check: bots submit instantly
    if (Date.now() - loadedAt.current < 3000) {
      setError('Submission too fast. Please try again.');
      return;
    }
    // Rate limiting
    if (cooldown > 0) {
      setError(`Please wait ${cooldown}s before submitting again.`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORM_ACCESS_KEY,
          subject: `Quote Request – ${formData.type} from ${formData.name}`,
          botcheck: false,
          ...formData,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setCooldown(QUOTE_COOLDOWN);
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Failed to submit. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setLoading(false);
    setError('');
    setFormData({ name: '', email: '', phone: '', company: '', type: 'bulk', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  const inputCls = "w-full px-4 py-2.5 rounded-lg border border-silver-200 bg-silver-50/50 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 focus:bg-white outline-none transition-all text-silver-800 text-sm placeholder:text-silver-300";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-silver-950/60 backdrop-blur-sm animate-fadeIn" onClick={resetAndClose} />

      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-scaleIn max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative overflow-hidden px-7 py-5 flex items-center justify-between flex-shrink-0 border-b border-silver-100">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-50 to-transparent" />
          <div className="relative">
            <h2 className="text-lg font-bold text-silver-900">Get a Quote</h2>
            <p className="text-silver-400 text-xs mt-0.5">We'll get back to you shortly</p>
          </div>
          <button onClick={resetAndClose}
            className="relative w-8 h-8 rounded-lg bg-silver-100 hover:bg-silver-200 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-silver-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-7 py-5">
          {submitted ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-lg font-bold text-silver-900 mb-1.5">Thank You!</h3>
              <p className="text-silver-500 text-sm max-w-xs mx-auto">We've received your request. Our team will get back to you shortly.</p>
              <button onClick={resetAndClose}
                className="mt-6 px-5 py-2.5 bg-silver-900 text-white rounded-lg text-sm font-semibold hover:bg-silver-800 transition-colors">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {/* Honeypot – hidden from real users; bots fill this in and Web3Forms rejects the submission */}
              <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-silver-600 mb-1.5 uppercase tracking-wider">Name *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-silver-600 mb-1.5 uppercase tracking-wider">Phone *</label>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={inputCls} placeholder="+91 99999 99999" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-silver-600 mb-1.5 uppercase tracking-wider">Email *</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputCls} placeholder="you@company.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-silver-600 mb-1.5 uppercase tracking-wider">Company</label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} className={inputCls} placeholder="Company name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-silver-600 mb-1.5 uppercase tracking-wider">I need *</label>
                  <select name="type" value={formData.type} onChange={handleChange} className={inputCls + ' bg-white'}>
                    <option value="bulk">Bulk Order (B2B)</option>
                    <option value="distributor">Distributorship</option>
                    <option value="custom">Custom Branding</option>
                    <option value="events">Event Supply</option>
                    <option value="retail">Retail Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-silver-600 mb-1.5 uppercase tracking-wider">Message *</label>
                <textarea name="message" required rows={3} value={formData.message} onChange={handleChange}
                  className={inputCls + ' resize-none'} placeholder="Tell us your requirements..." />
              </div>
              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">{error}</p>
              )}
              <button type="submit"
                disabled={loading || cooldown > 0}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-silver-900 text-white rounded-lg text-sm font-semibold hover:bg-silver-800 transition-all duration-200 mt-1 disabled:opacity-60 disabled:cursor-not-allowed">
                {loading ? 'Submitting…' : cooldown > 0 ? `Wait ${cooldown}s` : 'Submit Request'}
                {!loading && cooldown === 0 && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
